import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { upsertProviderKey, deleteProviderKey } from '../../../lib/enrichment/api';
import type { ProviderKeyMeta } from '../../../lib/enrichment/api';

interface Props {
  providerKey: string;
  providerName: string;
  current: ProviderKeyMeta | null;
  onClose: () => void;
  onSaved: () => void;
}

export function ByokConfigDrawer({ providerKey, providerName, current, onClose, onSaved }: Props) {
  const [apiKey, setApiKey] = useState('');
  const [notes, setNotes] = useState(current?.notes ?? '');
  const [enabled, setEnabled] = useState(current?.enabled ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await upsertProviderKey(providerKey, { api_key: apiKey, enabled, notes: notes || null });
      onSaved();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!current) return;
    if (!window.confirm('Rimuovere la chiave API? L\'integrazione verrà disabilitata immediatamente.')) return;
    setSaving(true);
    try {
      await deleteProviderKey(providerKey);
      onSaved();
      onClose();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-md max-w-md w-full text-[#37352f]">
        <div className="px-5 pt-5 pb-2">
          <h3 className="text-[15px] font-semibold">Configura {providerName}</h3>
          <p className="text-[12px] text-[#787774] mt-1">
            BYOK — chiave salvata cifrata, usata server-side.
            Paghi direttamente al provider, Flusia non aggiunge costi.
          </p>
        </div>

        <div className="px-5 py-3 space-y-3">
          <div>
            <label className="block text-[12px] font-medium mb-1">
              API key{' '}
              {current?.has_key && (
                <span className="text-[11px] text-[#9b9a97] font-normal">
                  · esistente, inserisci per sovrascrivere
                </span>
              )}
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={current?.has_key ? '••••••••••••••••' : 'Incolla qui la chiave'}
              className="w-full px-2.5 py-1.5 text-[13px] border border-[rgba(55,53,47,0.16)] rounded focus:outline-none focus:border-[#37352f]"
            />
          </div>

          <div>
            <label className="block text-[12px] font-medium mb-1">Note (opzionali)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Es. piano Pro $99/mese, key creata oggi"
              rows={2}
              className="w-full px-2.5 py-1.5 text-[13px] border border-[rgba(55,53,47,0.16)] rounded focus:outline-none focus:border-[#37352f] resize-none"
            />
          </div>

          <div className="flex items-center gap-2 pt-1">
            <button
              onClick={() => setEnabled((v) => !v)}
              className={`relative inline-flex w-9 h-5 rounded-full transition-colors ${
                enabled ? 'bg-[#37352f]' : 'bg-[rgba(55,53,47,0.16)]'
              }`}
              aria-label="toggle"
            >
              <span
                className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                  enabled ? 'translate-x-4' : ''
                }`}
              />
            </button>
            <span className="text-[13px]">{enabled ? 'Provider attivo' : 'Provider in pausa'}</span>
          </div>

          {error && (
            <div className="text-[12px] text-[#e03e3e] bg-[rgba(224,62,62,0.06)] px-2 py-1.5 rounded">
              {error}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-[rgba(55,53,47,0.09)] flex items-center justify-between">
          {current ? (
            <button
              onClick={onDelete}
              disabled={saving}
              className="text-[12px] text-[#e03e3e] hover:bg-[rgba(224,62,62,0.06)] px-2 py-1 rounded disabled:opacity-50"
            >
              Rimuovi chiave
            </button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-1">
            <button
              onClick={onClose}
              className="text-[13px] text-[#37352f] px-3 py-1.5 hover:bg-[rgba(55,53,47,0.06)] rounded"
            >
              Annulla
            </button>
            <button
              onClick={onSave}
              disabled={saving || (!current && !apiKey)}
              className="text-[13px] text-white bg-[#37352f] hover:bg-black px-3 py-1.5 rounded font-medium flex items-center gap-1.5 disabled:opacity-50"
            >
              {saving && <Loader2 className="w-3 h-3 animate-spin" />}
              Salva
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
